/**
 * `AW1` · La tabla de la pantalla «Banco de Contenido».
 *
 * Las seis columnas del PRD, el orden por columna, el paginado, el estado vacío
 * y el refresco sin recargar la página.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS TRES DECISIONES QUE ESTE ARCHIVO LLEVA CON MARCADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **No se importa `modules/**`.** Lo único que entra del repo son TIPOS de
 *   `contracts/read-api`, y el transporte es HTTP contra `admin-web`
 *   (`proveedor-datos.ts`). El grafo de `arquitectura.html` §6 lo prohíbe
 *   textual —`web-dev ──✗──▶ cualquier modules/`— y el motivo de fondo es que
 *   esta unidad es un **artefacto estático**: un import de `modules/read-api`
 *   compila, anda en el dev server de quien lo escribe, y no existe en
 *   producción, donde no hay Node ni credencial de base.
 *
 * ② **El orden y el paginado se PIDEN.** Todo lo que se ve en pantalla es la
 *   página que contestó el borde, en el orden en que la contestó. Este archivo
 *   no tiene un solo `.sort(`, `.slice(` ni `.filter(` sobre las filas, y el
 *   número de páginas sale de `total` —el total en la BASE— y nunca de
 *   `items.length`. Un click en un encabezado es una CONSULTA NUEVA.
 *
 * ③ **El refresco es esta pantalla preguntando cada N segundos.** Un
 *   `refetchInterval`. No hay WebSocket, ni SSE, ni suscripción: `contracts/read-api`
 *   expone `list()` y `close()` y nada más, así que no hay superficie por donde
 *   entre un push, y abrirla desde acá crearía un segundo camino hacia el mismo
 *   estado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LA ESCRITURA YA ESTÁ CABLEADA — `AW2` Y `AW3`
 *
 * El botón «Cargar documento» abre `modal-carga.tsx` (`AW2`) y las tres acciones
 * de fila viven en `acciones.tsx` (`AW3`). Los dos escriben por
 * `borde-escritura.ts`, que es `contracts/admin-api` sin el `actor` —el actor lo
 * pone `admin-web` desde la sesión, nunca el navegador—.
 *
 * **Este archivo no escribe nada por su cuenta**: lo único que hace es refrescar
 * la lista cuando el banco cambió, con `invalidar()`, y siempre DESPUÉS de que el
 * borde contestó. La tabla nunca saca ni agrega una fila por adelantado.
 */

import { useInvalidate, useTable } from "@refinedev/core"
import type { HttpError } from "@refinedev/core"
import type { SortColumn } from "@contracts/read-api/index.ts"
import { useCallback, useState } from "react"
import {
  COLUMNAS_ORDENABLES,
  DIRECCION_POR_DEFECTO,
  ORDEN_POR_DEFECTO,
  RECURSO_DOCUMENTOS,
} from "../proveedor-datos.ts"
import type { FilaDocumento } from "../proveedor-datos.ts"
import { useConfiguracion } from "../contexto-configuracion.tsx"
import { aLosBrazos, brazos, enumerar, sellosDeLosBrazos } from "../zonas.ts"
import { formatearEstado, formatearFecha, formatearFragmentos, formatearTema } from "./formato.ts"
import { ModalCarga } from "./modal-carga.tsx"
import { AccionesDeFila } from "./acciones.tsx"
import { Paginado } from "../paginado.tsx"

/**
 * Las seis columnas del PRD, en el orden en que las describe.
 *
 * `docs/prd/F1.md` §Pantalla «Banco de Contenido». El orden de este arreglo ES
 * el orden de la tabla: no hay otra lista en otro lado que pueda discrepar.
 *
 * `ordenablePor` es `SortColumn` o `null`, y no un booleano: así la columna
 * lleva encima POR QUÉ campo pide el orden, y el tipo impide inventar uno que
 * el contrato no acepte. Las tres que van en `null` no son un recorte de
 * producto — es que `contracts/read-api` acepta ordenar por esas tres y no más,
 * y coincide con lo que el PRD pide en §P1 («nombre, fecha, estado»).
 */
const COLUMNAS: readonly {
  readonly clave: string
  readonly encabezado: string
  readonly ordenablePor: SortColumn | null
  readonly numerica: boolean
}[] = [
  { clave: "name", encabezado: COLUMNAS_ORDENABLES.name, ordenablePor: "name", numerica: false },
  { clave: "topic", encabezado: "Tema / Categoría", ordenablePor: null, numerica: false },
  { clave: "state", encabezado: COLUMNAS_ORDENABLES.state, ordenablePor: "state", numerica: false },
  { clave: "uploadedAt", encabezado: COLUMNAS_ORDENABLES.uploadedAt, ordenablePor: "uploadedAt", numerica: false },
  { clave: "fragmentCount", encabezado: "Nº de fragmentos", ordenablePor: null, numerica: true },
  { clave: "acciones", encabezado: "Acciones", ordenablePor: null, numerica: false },
]

export function TablaBancoDeContenido() {
  const config = useConfiguracion()
  const [modalDeCargaAbierto, setModalDeCargaAbierto] = useState(false)

  /**
   * Volver a PEDIR la lista, después de una escritura.
   *
   * `invalidate` de Refine marca la consulta como vieja y `react-query` la vuelve
   * a pedir — es la misma consulta que ya hace el `refetchInterval` del `guard`
   * ③, disparada a mano para no esperar los N segundos. **No toca la caché con un
   * valor calculado acá**: no se saca una fila borrada ni se agrega una fila
   * cargada. La tabla sigue mostrando lo que contestó el borde, que es lo que
   * `guard` ② de `AW1` sostiene y lo que hace que un borrado que falló del otro
   * lado no se vea como exitoso.
   */
  const invalidate = useInvalidate()
  const invalidar = useCallback(() => {
    void invalidate({ resource: RECURSO_DOCUMENTOS, invalidates: ["list"] })
  }, [invalidate])

  const {
    result,
    tableQuery,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage,
  } = useTable<FilaDocumento, HttpError>({
    resource: RECURSO_DOCUMENTOS,

    /**
     * `guard` ② · `mode: "server"` en las dos.
     *
     * Es el default de Refine, y va escrito igual: es la decisión que este
     * archivo lleva con marcador, y `mode: "client"` es un cambio de una palabra
     * que haría que Refine se trajera todo y paginara en memoria — la
     * implementación exacta que el `guard` prohíbe— sin romper ningún tipo y sin
     * que ningún examen de `R1` lo note, porque pasa de este lado del borde.
     */
    pagination: { currentPage: 1, pageSize: config.pageSize, mode: "server" },
    sorters: {
      initial: [{ field: ORDEN_POR_DEFECTO, order: DIRECCION_POR_DEFECTO }],
      mode: "server",
    },

    /**
     * «Sin búsqueda ni filtro» — `F1.md` §Pantalla «Banco de Contenido».
     *
     * `mode: "off"` explícito y no simplemente no usar filtros: deja escrito que
     * la ausencia es del PRD y no un pendiente que alguien vaya a completar.
     */
    filters: { mode: "off" },

    queryOptions: {
      /**
       * `guard` ③ · ACÁ está el refresco, y es todo lo que es.
       *
       * La pantalla vuelve a PREGUNTAR cada `refrescoMs`. Es lo que cumple «la
       * tabla refresca los estados sin recargar la página» (`F1.md` §P0-2) y lo
       * que hace que un documento `en proceso` se vea pasar a `indexado`, con su
       * nº de fragmentos, sin que el operador toque nada.
       */
      refetchInterval: config.refrescoMs,

      /**
       * No se pregunta con la pestaña en segundo plano.
       *
       * Nadie está mirando: son consultas contra `admin-web` que no le ahorran
       * un segundo a nadie.
       */
      refetchIntervalInBackground: false,

      /**
       * Y al VOLVER a la pestaña se pregunta una vez, sin esperar el intervalo.
       *
       * **Va explícito porque el default efectivo es `false`, y no es el de
       * `react-query`.** `@tanstack/react-query` 5.101.4 trata el valor ausente
       * como «refrescar si está viejo» (`query-core/queryObserver.js:450-456`:
       * `value !== false && isStale(...)`), pero **`@refinedev/core` 5.0.12
       * construye su `QueryClient` con `refetchOnWindowFocus: false`**
       * (`dist/index.cjs:8352`), antes de mezclar cualquier config del usuario.
       * O sea: sin esta línea, no se refresca al volver — medido, no deducido.
       *
       * **Esta línea es la otra mitad de `refetchIntervalInBackground: false`.**
       * Las dos juntas son la decisión completa; una sola es un agujero. Apagar
       * el intervalo en segundo plano hace que los datos envejezcan
       * **proporcionalmente a cuánto estuvo afuera el operador, sin techo**, y el
       * regreso a la pestaña es exactamente el instante en que los mira. El caso
       * que esto cubre no es el default de 5 s —ahí es invisible— sino el
       * `refrescoMs` alto que `configuracion.ts:74-79` anticipa y argumenta.
       *
       * **Lo que cuesta, dicho:** un pedido por cada vuelta a la pestaña, y sólo
       * si la consulta ya está vieja. Es estrictamente menos tráfico que dejar el
       * intervalo corriendo en segundo plano, que es la alternativa que le daría
       * la misma frescura.
       *
       * Sigue siendo la pantalla PREGUNTANDO —`guard` ③—: es un `fetch` más
       * disparado por un evento del navegador, no una conexión persistente ni un
       * push. `contracts/read-api` sigue sin superficie de suscripción.
       *
       * Pinchado por `pruebas/tabla-refresco.test.tsx`, que mide el delta al
       * volver con un `refrescoMs` de una hora —así ninguna consulta puede venir
       * del intervalo— y que además falla en rojo el día que Refine deje de
       * forzar `false`, para que este comentario no envejezca en silencio.
       */
      refetchOnWindowFocus: true,
    },
  })

  const total = result.total ?? 0
  const documentos = result.data
  const ordenActual = sorters[0]

  /**
   * `isLoading` es sólo la PRIMERA carga; los refrescos de `guard` ③ no lo
   * levantan. Si lo hicieran, la tabla parpadearía a «Cargando…» cada N segundos
   * y el operador perdería la fila que estaba mirando.
   */
  const cargandoPrimeraVez = tableQuery.isLoading
  const refrescando = tableQuery.isFetching && !tableQuery.isLoading

  /**
   * Un click en un encabezado PIDE el orden de nuevo, no reordena lo que hay.
   *
   * Vuelve a la página 1 porque el orden cambió: quedarse en la página 4 de un
   * orden distinto muestra un tramo del medio que el operador no pidió y que no
   * tiene relación con lo que estaba viendo.
   *
   * Reemplaza el orden en vez de apilarlo —`[{…}]` y no `[…sorters, {…}]`—
   * porque `ListQuery.sort` acepta UNA columna: apilar generaría un orden que la
   * pantalla mostraría como activo y que el borde no puede cumplir.
   */
  const alOrdenarPor = (columna: SortColumn) => {
    const yaOrdenadaPorEsta = ordenActual !== undefined && ordenActual.field === columna
    const direccion = yaOrdenadaPorEsta && ordenActual.order === "asc" ? "desc" : "asc"
    setSorters([{ field: columna, order: direccion }])
    setCurrentPage(1)
  }

  const direccionDe = (columna: SortColumn): "asc" | "desc" | null => {
    if (ordenActual === undefined || ordenActual.field !== columna) return null
    return ordenActual.order
  }

  return (
    <section className="banco" aria-labelledby="banco-titulo">
      <header className="banco__encabezado">
        {/*
          `F5-CA-09` (PRD:828), textual: *«muestra el título «Banco de contenido»»*.
          ⛔ La minúscula no es estilo: el criterio es **copia exacta**, y acá decía
          «Banco de Contenido». Se cambió por el PRD y NO por el rótulo del menú.

          ⚠️ Y queda un desencuentro que esta etapa reporta porque NO es de su
          territorio: `App.tsx` —el marco y las rutas— sigue rotulando la pantalla
          «Banco de Contenido» con mayúscula, así que el mismo destino se escribe
          de dos formas. Va en el `verdict`.
        */}
        <h1 id="banco-titulo">Banco de contenido</h1>

        {/* `AW2` · abre el modal «Cargar documento». */}
        <button
          type="button"
          className="boton boton--primario"
          onClick={() => setModalDeCargaAbierto(true)}
        >
          Cargar documento
        </button>
      </header>

      {/*
        ⭐⭐ `REQ-R1.1-F3-CA-03` (PRD:511) y `REQ-R1.1-F3-CA-04` (PRD:512) — EL AVISO
        DE LOS DOS BRAZOS. Los dos son de ESTA pantalla y estaban sin construir.

        ⛔ Y estuvieron a punto de no estarlo por un razonamiento que hay que dejar
        escrito, porque es el error que este comentario previene: se leyó que
        `configuracion-del-chatbot` ya dibuja «Aplica a los dos brazos (Híbrido y
        Puro) y cada cambio queda vigente apenas se guarda.» (`F3-CA-11`, PRD:519)
        y se concluyó que el aviso era **del marco** y que repetirlo acá duplicaba.
        **Es falso, y el error es de lectura, no de criterio:** son DOS criterios
        distintos con DOS copys distintos —`CA-11` es de Configuración, `CA-03` es
        del Banco— y el que manda para ésta es el de su propia fila. La lista de
        criterios lo decía y está a la vista: `specs/R1.1-derivadas/
        ui-banco-de-contenido.md:16-17`. ⇒ el aviso va ACÁ, y el de Configuración no
        se toca.

        CA-03 · el texto sale de `PRD:511`, y **CUIDADO CON LA FUENTE**: el boceto
        (`BancoDeContenido.dc.html:69`) escribe «Lo que cambies **acá**…» y el PRD
        escribe «Lo que cambies **aquí**…». Gana el PRD —el orden de fuentes es
        PRD > sistema-visual > bocetos— así que el renglón dice «aquí».
        ⚠️ Y el `<strong>` sobre la primera cláusula es del boceto, que la dibuja
        en negrita; el texto completo es el mismo.

        CA-04 · **NO lleva botón de cierre, y eso es el criterio, no un olvido.** No
        se le pone un `×` «por consistencia con otros avisos»: el criterio afirma
        que el aviso NO se puede cerrar.

        ⛔⛔ **Y NO lleva `role="note"`. No es estilo, es que rompería un examen.**
        `aw47-declaracion-del-banco.test.tsx` toma el **PRIMER** `role="note"` del
        documento (`querySelector`) y le afirma los términos de §5.11. Esta caja va
        ANTES de la nota §5.11 —la posición del boceto— así que si llevara ese rol
        se convertiría en «la» nota de la pantalla y `AW47` se pondría rojo por una
        razón que no tiene nada que ver con este cambio. La primitiva `.nota` con
        `role="note"` es **una por pantalla** (`estilos.css:1167`) y ésa ya es la de
        §5.11, tres bloques más abajo.

        ⚠️ **`nota-brazo` es la clase del boceto y hoy NO tiene regla en
        `estilos.css`** —tampoco la tiene `.configuracion-chatbot__nota`, que es su
        hermana—, y la fila ámbar de §1.3 (`#5c3c0a` sobre `#fff4e0`, borde
        `1px #f0d9a8`) está anotada como NO tokenizada (`estilos.css:5065-5066`).
        Acá NO se le ponen colores en línea a propósito: el hex escrito a mano lo
        acusan los jueces (`gates/respuestas-y-rotulos.spec.ts:1742`, regla (b) de
        `J10`; `gates/marco-y-anchos.spec.ts:1608`), y la hoja es de otro
        territorio. ⇒ **el aviso se construye con el texto y la clase, y su regla
        queda pendiente con dueño: va en el `verdict`.**
      */}
      <p className="nota-brazo">
        {/*
          ⟦rastro · ADR-0082 · ADR-0402 · 30/9/2026 · R3-H-005⟧ Acá decía ~~«KAI · Brazo Híbrido y
          KAI · Brazo Puro»~~ con «KAI» LITERAL y los dos brazos escritos a mano. `R2-E3`/`I-11`:
          la instancia la pone la declaración. `sellosDeLosBrazos()` (`zonas.ts:413`) ya existía
          para esto —«para los textos que los nombran juntos»— y **no lo usaba nadie**; de paso,
          la lista de brazos también sale de la declaración en vez de estar escrita.
          ⚠️ Ningún caso de R3 mira esta línea: se arregla porque es el MISMO defecto, y dejarlo
          es dejar sembrado el próximo hallazgo (ADR-0402 §③).
        */}
        {/*
          ⟦rastro · ADR-0082 · 30/9/2026 · fix/consola-literales-de-brazo⟧ ~~«aplica a los dos
          brazos:»~~ fijo, que en `OLI` (un brazo) decía «los dos brazos: OLI · Brazo Híbrido».
          Sale de `aLosBrazos()`; con un solo brazo no se repite su sello después de nombrarlo.
        */}
        {brazos().length >= 2 ? (
          <>
            <strong>Lo que cambies aquí aplica {aLosBrazos()}:</strong>{" "}
            {enumerar(sellosDeLosBrazos())}.
          </>
        ) : (
          <strong>Lo que cambies aquí aplica {aLosBrazos()}.</strong>
        )}
      </p>

      {/*
        ⛔⭐ `AW47` · §5.11 — LA LIMITACIÓN QUE HAY QUE DECLARAR, Y ACÁ ES DONDE
        EL OPERADOR LA VE.

        `PRD_R1.md` R1-F3 §5.11 no dice «no se construye versionado» y ya. Dice
        textual: *«**Lo que hay que dejar dicho**, porque afecta al ensayo y no al
        producto: si alguien reemplaza un documento a mitad de campaña, esa parte
        del corpus **deja de ser reconstruible**, y el análisis que el BID va a
        hacer meses después no va a poder saber con qué material respondió el
        chatbot en una fecha dada. La contención es procedimental y hay que
        operarla: quién puede cargar y borrar, y que los cambios del banco durante
        la campaña queden **anotados fuera del sistema, con fecha**.»*

        ⛔ **POR QUÉ NO ALCANZA CON LO QUE YA ESTABA**, que es la salida cómoda de
        esta etapa: `acciones.tsx` ya dice «no hay historial ni papelera» adentro
        del modal «Eliminar documento». Ese texto es cierto y **no es esto**, por
        tres razones que no se arreglan moviéndolo:

          ① habla del **borrado** y `§5.11` habla del **reemplazo** — que en este
             producto es borrar y volver a cargar, porque `§5.2` prohíbe dos
             documentos con el mismo nombre;
          ② vive **después** de que el operador ya decidió y abrió el diálogo,
             cuando la información ya no le sirve para decidir;
          ③ no dice una palabra de la **contención procedimental**, que es la
             mitad de `§5.11` que efectivamente hay que operar — y es lo único que
             reemplaza a la auditoría de contenido mientras no la haya.

        ⇒ va en la PANTALLA, antes de tocar nada. Lo custodia
        `gates/pantalla-del-banco.spec.ts` `B3`, que además falla si la
        declaración se muda a un `Dialogo`.

        ⚠️ **Y es una declaración, no una advertencia de acción.** No dice «cuidado
        al borrar» ni intenta disuadir: el riesgo está **aceptado por escrito por
        el cliente** (`D11`), así que un texto que sonara a alarma le pediría al
        operador que reconsidere algo que ya está decidido arriba de él. Dice qué
        se pierde y qué hay que hacer en su lugar.

        La primitiva es `.nota` con `role="note"` (`AW32`) —una por pantalla, y
        ésta es la del Banco—, y va **FUERA del `<header>`** a propósito: es la
        posición que `AW32` estandarizó, la que deja la nota DESPUÉS del filete del
        `<h1>`. Su par de color (`--nota` sobre `--nota-tenue`) ya está medido en
        `gates/sistema-visual.spec.ts` `PARES_A_MEDIR` — **5,56:1, sobre el mínimo
        AA de 4,5:1**— así que esta caja no estrena ninguna superficie sin
        instrumento, que es la forma exacta en que entró el defecto de contraste
        de `R1-F3-27`.

        ⚠️ **EL DIALECTO ES TUTEO, Y NO ES UNA PREFERENCIA — está medido en esta
        misma pantalla.** `ADR-0268` (7/9/2026) midió que el copy que lee un
        participante está en «tú» 14 veces y en «vos» ninguna, y decidió conjugar
        el borrador de Producto en «tú». Acá se midió lo propio, sobre las TRES
        formas imperativas que esta pantalla ya tenía, y las tres son tuteo:
        `acciones.tsx:259` («**Avisa** a soporte técnico»), `modal-carga.tsx:398`
        («**carga** lotes separados») y este mismo archivo, en el renglón de la
        página vacía («**vuelve** a una página anterior»). Voseo, cero. Esta nota
        se escribió primero con «anotá» y se corrigió por eso.

        ⛔ **Y queda un desencuentro REAL que esta etapa no puede tocar y reporta:**
        el texto de `modules/knowledge/src/errors.ts` —el que «Ver motivo» dibuja
        en esta misma pantalla— está en VOSEO («Reintentá», «avisá a soporte»,
        «Esperá unos minutos»). O sea que la misma pantalla mezcla los dos
        dialectos, y el que desentona es el que viene del módulo. No es de este
        territorio.
      */}
      {/*
        ⛔⭐ `F3-CA-05` (PRD:513) ES COPIA EXACTA, y por eso el renglón de abajo se
        reescribió: el criterio pide que *«en Banco de contenido se lee «Sin
        versionado ni historial de contenido. Reemplazar un documento a mitad de
        campaña deja esa parte del corpus no reconstruible. Anota cada carga y cada
        eliminación fuera del sistema, con su fecha.»»* — y el texto que había
        intercalaba la justificación del BID («meses después no se va a poder saber
        con qué material respondió…») y la partícula «procedimental», así que la
        frase del PRD **no aparecía entera**.

        ⇒ Ahora el párrafo ES la frase del PRD, y las dos mitades quedan donde `B3`
        las exige: la consecuencia (`reconstruible` + `campaña`) y la contención
        (`fuera del sistema` + `fecha`). `gates/pantalla-del-banco.spec.ts:278-279`
        mide por FRASE y no por cadena, así que el cambio no lo mueve; `AW47` pide
        los mismos cinco términos (`aw47-…test.tsx:139-143`). Verificado antes de
        tocar: ninguno de los dos se pone rojo.

        ⚠️ Lo que se fue del renglón NO se perdió: la justificación del BID sigue
        en `PRD_R1.md` §5.11 —que es la fuente— y en el bloque de arriba. Se sacó de
        la pantalla porque *el criterio pide copia exacta*, no porque sobre.
      */}
      <p className="nota" role="note">
        Sin versionado ni historial de contenido. Reemplazar un documento a mitad de campaña deja
        esa parte del corpus no reconstruible. Anota cada carga y cada eliminación fuera del sistema,
        con su fecha.
      </p>

      {/*
        `AW2` · El modal se MONTA al abrirse y se DESMONTA al cerrarse.

        No queda montado y escondido con CSS, y la razón es UNA: sus restricciones
        de carga se piden al abrirlo (`guard` ②), y esos dos valores son
        configuración de instancia que el mismo Management Tool edita. Un modal
        que se montara una sola vez las pediría una sola vez, y quien acabe de
        subir el tamaño máximo seguiría rechazando con el viejo.

        (Antes acá decía además que un modal con `display:none` «sigue siendo
        tabulable en algunos navegadores». **Es falso** —`display:none` saca del
        orden de tabulación en todos los navegadores actuales— y se sacó en vez
        de dejarlo sosteniendo una decisión que ya se sostiene sola.)
      */}
      {modalDeCargaAbierto && (
        <ModalCarga
          onCerrar={() => setModalDeCargaAbierto(false)}
          alTerminarUnLote={invalidar}
        />
      )}

      {/*
        El estado del refresco de `guard` ③, anunciado sin robar el foco.
        `aria-live="polite"` para que un lector de pantalla lo mencione cuando
        termine lo que está leyendo, y no en el medio.
      */}
      <p className="banco__estado-carga" aria-live="polite">
        {refrescando ? "Actualizando estados…" : ""}
      </p>

      {tableQuery.isError && (
        <p className="banco__error" role="alert">
          No se pudieron cargar los documentos. {tableQuery.error?.message ?? ""}
        </p>
      )}

      {/*
        ⛔ Regla 8 · **«todavía no sé» no se puede ver igual que «no hay».** Acá
        decía `className="banco__vacio"`, la MISMA zona punteada que «Aún no hay
        documentos cargados». `.banco__estado-carga` ya existía —la usaba sólo la
        línea de «Actualizando estados…» del refresco— y es la que corresponde
        también a la PRIMERA carga.
      */}
      {cargandoPrimeraVez && <p className="banco__estado-carga">Cargando documentos…</p>}

      {/*
        ESTADO VACÍO · `F1.md` §Pantalla «Banco de Contenido».

        Se decide por `total`, que es el total en la BASE, y no por
        `documentos.length`: con `items.length` una página 3 que quedó fuera de
        rango se vería como «no hay documentos cargados» cuando el banco está
        lleno. Y se exige que ya no esté cargando ni haya error, para no mostrar
        «aún no hay documentos» mientras todavía no se sabe.
      */}
      {!cargandoPrimeraVez && !tableQuery.isError && total === 0 ? (
        <div className="banco__vacio">
          <p>Aún no hay documentos cargados</p>
          {/* `AW2` · el mismo modal, desde el estado vacío. */}
          <button
            type="button"
            className="boton boton--primario"
            onClick={() => setModalDeCargaAbierto(true)}
          >
            Cargar documento
          </button>
        </div>
      ) : null}

      {!cargandoPrimeraVez && total > 0 && (
        <>
          <div className="tabla-rodante">
            <table className="banco__tabla">
              <caption className="banco__caption">
                Documentos cargados en el banco de contenido.
              </caption>
              <thead>
                <tr>
                  {COLUMNAS.map((columna) => {
                    /**
                     * En una constante para que TypeScript estreche el `null` una
                     * sola vez y el resto del bloque trabaje con `SortColumn`. Es
                     * lo que hace que `alOrdenarPor` no pueda recibir una columna
                     * que el contrato no acepta: sale del tipo, no de un chequeo.
                     */
                    const ordenablePor = columna.ordenablePor
                    const direccion = ordenablePor === null ? null : direccionDe(ordenablePor)

                    return (
                      <th
                        key={columna.clave}
                        scope="col"
                        /**
                         * `aria-sort` es cómo un lector de pantalla sabe por qué
                         * columna está ordenada la tabla. Sin esto el orden es una
                         * flechita que sólo existe para quien la ve.
                         */
                        aria-sort={
                          ordenablePor === null
                            ? undefined
                            : direccion === "asc"
                              ? "ascending"
                              : direccion === "desc"
                                ? "descending"
                                : "none"
                        }
                      >
                        {ordenablePor === null ? (
                          columna.encabezado
                        ) : (
                          <button
                            type="button"
                            className="banco__orden"
                            onClick={() => alOrdenarPor(ordenablePor)}
                          >
                            {columna.encabezado}
                            <span aria-hidden="true" className="banco__flecha">
                              {direccion === "asc" ? "▲" : direccion === "desc" ? "▼" : "↕"}
                            </span>
                          </button>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>

              <tbody>
                {/*
                  Una página vacía con documentos en el banco.

                  Pasa cuando el banco se achica bajo los pies del operador: está
                  mirando la página 3, alguien borra documentos —desde otra sesión,
                  o por la API, que `arquitectura.html` §2 muestra como camino real:
                  «una persona de Elige, con Postman»— y la página 3 deja de
                  existir. Sin este renglón la tabla se ve como un cuerpo vacío sin
                  ninguna explicación, que se lee como que se rompió.

                  No se corrige la página sola: mover al operador de página sin que
                  lo haya pedido, y encima por algo que pasó en otra sesión, le
                  cambia lo que está mirando abajo de la vista. Se le dice qué pasó
                  y el paginado de abajo sigue funcionando.
                */}
                {documentos.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNAS.length} className="banco__vacio">
                      Esta página ya no tiene documentos. El banco tiene {total} en
                      total: vuelve a una página anterior.
                    </td>
                  </tr>
                )}

                {/*
                  `documentos` se recorre TAL COMO VINO. Sin `.sort()`, sin
                  `.slice()`, sin `.filter()`: ésa es la mitad visible de `guard` ②.
                */}
                {documentos.map((documento) => (
                  <tr key={documento.id}>
                    <td>{documento.name}</td>
                    <td>{formatearTema(documento.topic)}</td>
                    <td>
                      {/*
                        ⭐ El DATO va LITERAL y el RÓTULO va traducido: son dos cosas
                        distintas y por eso conviven acá.

                        · `data-estado={documento.state}` va CRUDO, tal como lo define
                          `contracts/knowledge` —incluido el espacio de «en proceso»—:
                          de ese atributo cuelgan el color Y el patrón de borde
                          (`estilos.css:1068-1087`), que es lo que hace que el estado no
                          se distinga sólo por tono. Tocarlo dejaría al chip sin estilo.

                        · el TEXTO VISIBLE sale de `formatearEstado()`, que es donde la
                          traducción tiene dueño. El chip tiene que decir «Listo» y no
                          «indexado»: lo piden `F3-CA-23` (PRD:531) y `F5-CA-80`
                          (PRD:899, «dice «Listo»»), y el boceto lo dibuja así
                          (`BancoDeContenido.dc.html:75`).

                        ⛔ Este renglón pintaba el literal crudo, y con
                        `text-transform: uppercase` encima el chip leía «INDEXADO»: el
                        e2e de `CA-23` no podía pasar de ninguna manera. No era una
                        decisión de estilo, era un criterio sin cumplir.
                      */}
                      <span className="banco__estado" data-estado={documento.state}>
                        {formatearEstado(documento.state)}
                      </span>
                    </td>
                    <td>{formatearFecha(documento.uploadedAt)}</td>
                    <td className="banco__numero">{formatearFragmentos(documento.fragmentCount)}</td>
                    <td>
                      {/*
                        `AW3` · las tres acciones de la fila, en `acciones.tsx`.

                        CUÁNDO se ofrece cada una vive allá y no acá: «Ver motivo» y
                        «Reintentar» pasan por `seOfreceVerMotivo()` y
                        `sePuedeReintentar()`, que son el `guard` ③ de `AW3` («sólo
                        sobre un documento en `error`»). Tenerlo en un solo archivo
                        es lo que impide que la tabla y las acciones respondan
                        distinto a la misma pregunta.

                        `alCambiarElBanco` es `invalidar`: la lista se vuelve a
                        PEDIR después de que el borde contestó. Nunca antes, y nunca
                        calculando el resultado acá.
                      */}
                      <AccionesDeFila documento={documento} alCambiarElBanco={invalidar} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            PAGINADO · `guard` ②.

            El número de páginas NO se deriva de `documentos.length` en ningún
            lado: por eso el paginado dice la verdad con el banco real y no sólo
            con los veinte documentos de una demo.

            ⭐ RASTRO · `AW31` · 28/8/2026 — acá había una barra escrita a mano,
            una de OCHO copias. Ahora es la primitiva `src/paginado.tsx`, que hace
            la cuenta con `total` y `pageSize` en vez de recibir el `pageCount` de
            Refine ya calculado: **un número que se puede pasar mal es un `guard`
            que se puede romper en silencio**, y los dos valores salen igual del
            mismo `total` del borde.
          */}
          <Paginado
            etiqueta="documentos"
            pagina={currentPage}
            total={total}
            tamanoDePagina={config.pageSize}
            unidad={{ singular: "documento", plural: "documentos" }}
            onPagina={setCurrentPage}
          />
        </>
      )}
    </section>
  )
}
